import { makeAutoObservable } from 'mobx'
import dayjs from 'dayjs'
import { DayjsFormats, type IDays } from 'shared/lib'
import { type IParamsAdvancedSchedule } from 'shared/ui/Schedule/ui/AdvancedScheduling'
import { WeekDaysSelectorStore } from 'shared/ui/Schedule/ui/WeekDaysSelector'
import { getDefaultQuarterTime } from 'shared/ui/Schedule/utils/getDefaultQuarterTime'
import { getSendTextByDays } from 'shared/ui/Schedule/utils/getSendTextByDays'

export type IInitAdvancedScheduling = {
  days: IDays[]
  time: string
  isAdvancedScheduling: boolean
}

export class AdvancedSchedulingStore {
  private _toggleCallBack: ((status: boolean) => void) | null = null

  constructor(toggleCallBack?: (status: boolean) => void) {
    makeAutoObservable(this)

    this._toggleCallBack = toggleCallBack || null
  }

  private _isRequiredAdvancedSchedule = false
  private _isAdvancedScheduleDisabled = false

  private _isAdvancedScheduling = false

  private _dropdownId = ''
  private _defaultTime = getDefaultQuarterTime()

  private _weekDaysSelectorStore = new WeekDaysSelectorStore()

  private _date: Date | null = this.defaultTime

  private _schedulingRecipientsMax = 0
  private _schedulingRecipients = 0

  get isEnabled() {
    return this.isRequiredAdvancedSchedule || this.isAdvancedScheduling
  }

  get weekDaysSelectorStore() {
    return this._weekDaysSelectorStore
  }

  get dropdownId() {
    return this._dropdownId
  }
  get date() {
    return this._date
  }

  get defaultTime() {
    return this._defaultTime
  }

  get isAdvancedScheduling() {
    return this._isAdvancedScheduling
  }

  get isRequiredAdvancedSchedule() {
    return this._isRequiredAdvancedSchedule
  }

  get isAdvancedScheduleDisabled() {
    return this._isAdvancedScheduleDisabled
  }

  get schedulingRecipientsMax() {
    return this._schedulingRecipientsMax
  }

  get schedulingRecipients() {
    return this._schedulingRecipients
  }

  get time() {
    return this.date || this.defaultTime
  }

  get advancedSchedule(): IParamsAdvancedSchedule {
    if (this.isEnabled) {
      return {
        days: this.weekDaysSelectorStore.selectedDays,
        time: dayjs(this.time).format(DayjsFormats.time3),
      }
    }
    return {
      days: [],
      time: dayjs(this.defaultTime).format(DayjsFormats.time3),
    }
  }

  get advancedText() {
    if (this.isEnabled) {
      return `Send only ${getSendTextByDays(this.weekDaysSelectorStore.selectedDays)}`
    }
    return ''
  }

  get advancedTextTime() {
    if (this.isEnabled) {
      return `At ${dayjs(this.time).format(DayjsFormats.time)}`
    }
    return ''
  }

  get switchTooltipLabel() {
    return 'Disabled when “Restrict sending hours” is active'
  }

  get buttonTooltipLabel() {
    return 'Advanced scheduling is automatically activated when the number of contacts exceeds the daily limit'
  }

  init = ({ days, isAdvancedScheduling }: IInitAdvancedScheduling) => {
    if (isAdvancedScheduling) {
      this.weekDaysSelectorStore.init({ days })
      this._isAdvancedScheduling = isAdvancedScheduling
    }
  }

  initDefault = () => {
    this._defaultTime = getDefaultQuarterTime()
  }

  toggleAdvancedScheduling = () => {
    if (this.isRequiredAdvancedSchedule || this.isAdvancedScheduleDisabled) {
      return
    }

    this._isAdvancedScheduling = !this.isAdvancedScheduling
    this._toggleCallBack?.(this._isAdvancedScheduling)
  }

  setDate = (date: Date | null) => {
    this._defaultTime = getDefaultQuarterTime(date)
    this._date = date
  }

  setDropdownId = (dropdownId: string) => {
    this._dropdownId = dropdownId
  }

  setRequiredAdvancedSchedule = (value: boolean) => {
    this._isRequiredAdvancedSchedule = value
  }

  setSchedulingRecipientsMax = (value: number) => {
    this._schedulingRecipientsMax = value
  }

  setSchedulingRecipients = (value: number) => {
    this._schedulingRecipients = value
  }

  setAdvancedScheduleDisabled = (status: boolean) => {
    this._isAdvancedScheduleDisabled = status
  }
}
